import React, { useMemo } from "react";
import styled from "styled-components";
import { Button, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { THEME_MAP } from "../theme";

const BASE_STATUS_COLORS = {
	completed: "#2fbf59", // more vivid green
	updated: "#f5b400", // brighter, cleaner yellow
	incomplete: THEME_MAP["color-danger-600"] || "#d32f2f"
};

const withAlpha = (hex, alpha) => {
	if (typeof hex !== "string" || !hex.startsWith("#")) {
		return hex;
	}
	const normalized = hex.replace("#", "");
	if (normalized.length !== 6) {
		return hex;
	}
	const a = Math.round(alpha * 255)
		.toString(16)
		.padStart(2, "0");
	return `#${normalized}${a}`;
};

export const STATUS_BACKGROUND_MAP = {
	completed: withAlpha(BASE_STATUS_COLORS.completed, 0.22), // brighter and less faded green
	updated: withAlpha(BASE_STATUS_COLORS.updated, 0.16), // bright but a touch lighter
	incomplete: THEME_MAP["color-basic-200"] || withAlpha(BASE_STATUS_COLORS.incomplete, 0.18) // match header background
};

const CHIP_BACKGROUND_MAP = {
	completed: withAlpha(BASE_STATUS_COLORS.completed, 0.30), // stronger chip to match vivid row
	updated: withAlpha(BASE_STATUS_COLORS.updated, 0.18), // slightly more solid than row for visibility
	incomplete: withAlpha(BASE_STATUS_COLORS.incomplete, 0.27) // bring back subtle red for chip only
};

export const STATUSES = [
	{ key: "completed", label: "Completed", color: BASE_STATUS_COLORS.completed },
	{ key: "updated", label: "Updated", color: BASE_STATUS_COLORS.updated },
	{ key: "incomplete", label: "Incomplete", color: BASE_STATUS_COLORS.incomplete }
];

export const STATUS_COLOR_MAP = STATUSES.reduce((acc, status) => {
	acc[status.key] = status.color;
	return acc;
}, {});

export function BarStatusFilters({ selectedStatuses, onChange }) {
	const selectedSet = useMemo(() => {
		if (selectedStatuses instanceof Set) {
			return selectedStatuses;
		}
		return new Set(Array.isArray(selectedStatuses) ? selectedStatuses : []);
	}, [selectedStatuses]);

	const handleToggle = (key) => {
		const next = new Set(selectedSet);
		if (next.has(key)) {
			next.delete(key);
		} else {
			next.add(key);
		}
		onChange?.(next);
	};

	const handleClear = () => {
		onChange?.(new Set());
	};

	return (
		<Container>
			<div className="chips">
				{STATUSES.map((status) => {
					const isActive = selectedSet.has(status.key);
					return (
						<Button
							key={status.key}
							variant={isActive ? "contained" : "outlined"}
							size="small"
							onClick={() => handleToggle(status.key)}
							className="chip-button"
							sx={{
								borderColor: status.color,
								color: isActive ? (THEME_MAP["color-basic-900"] || "#1f1f1f") : status.color,
								backgroundColor: isActive ? CHIP_BACKGROUND_MAP[status.key] : "transparent",
								"&:hover": {
									backgroundColor: isActive ? CHIP_BACKGROUND_MAP[status.key] : THEME_MAP["color-basic-100"]
								},
								textTransform: "none",
								fontWeight: 600
							}}
						>
							{status.label}
						</Button>
					);
				})}
			</div>
			{selectedSet.size > 0 && (
				<div className="clear">
					<IconButton size="small" onClick={handleClear} title="Clear status filters">
						<Close fontSize="small" />
					</IconButton>
				</div>
			)}
		</Container>
	);
}

const Container = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 0;

	.chips {
		display: flex;
		gap: 8px;
		flex-wrap: nowrap;
	}

	.chip-button {
		min-width: auto;
	}

	.clear {
		display: flex;
		align-items: center;
	}
`;
